/**
 * Matchday Planning Page (Spieltagsplanung)
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Building2, CalendarDays } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { usePartnerAuth } from '../contexts/PartnerAuthContext';
import PlanningHeader from '../components/matchdayPlanning/PlanningHeader';
import PlanningTimeline from '../components/matchdayPlanning/PlanningTimeline';
import SegmentModal from '../components/matchdayPlanning/SegmentModal';
import BulkAssignmentModal from '../components/matchdayPlanning/BulkAssignmentModal';
import VehicleAssignments from '../components/matchdayPlanning/VehicleAssignments';
import StaffAssignments from '../components/matchdayPlanning/StaffAssignments';
import RouteMap from '../components/matchdayPlanning/RouteMap';
import {
  MatchdayPlanning,
  PlanningSegment,
  PlanningUIState,
  SegmentFormData,
  LocalMatchdayInfo,
  MatchInfo,
  ClubInfo,
  TransportationInfo,
  TravelerInfo,
  PartnerInfo,
  Vehicle,
  PartnerDriver,
  PartnerDriverInfo,
  VehicleAssignment,
  StaffAssignment,
  StaffRole
} from '../types/matchdayPlanning';
import { matchdayPlanningApi } from '../services/matchdayPlanningApi';
import { matchdayPlanningEntitiesApi } from '../services/matchdayPlanningEntitiesApi';
import '../styles/MatchdayPlanning.css';

const MatchdayPlanningPage: React.FC = () => {
  const { t } = useTranslation(['common']);
  const { planId: planIdStr } = useParams<{ planId: string }>();
  const planId = planIdStr ? parseInt(planIdStr) : undefined;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { user: clubUser } = useAuth();
  const { user: partnerUser } = usePartnerAuth();

  const isPartnerUser = partnerUser !== null;

  const [planning, setPlanning] = useState<MatchdayPlanning | null>(null);
  const [segments, setSegments] = useState<PlanningSegment[]>([]);
  const [vehicleAssignments, setVehicleAssignments] = useState<VehicleAssignment[]>([]);
  const [bulkAssignment, setBulkAssignment] = useState<VehicleAssignment | null>(null);
  const [staffAssignments, setStaffAssignments] = useState<StaffAssignment[]>([]);
  const [arrivalLeadTime, setArrivalLeadTime] = useState<number>(90);
  const [showDrivingTimes, setShowDrivingTimes] = useState<boolean>(clubUser?.showDrivingTimes ?? false);
  const [uiState, setUIState] = useState<PlanningUIState>({
    isLoading: true,
    error: null,
    selectedSegmentId: null,
    isModalOpen: false,
    modalMode: 'create'
  });

  const [eventType, setEventType] = useState<'local' | 'match' | null>(null);
  const [selectedLocalMatchdayId, setSelectedLocalMatchdayId] = useState<number | undefined>(undefined);
  const [selectedMatchId, setSelectedMatchId] = useState<number | undefined>(undefined);
  const [localMatchdays, setLocalMatchdays] = useState<LocalMatchdayInfo[]>([]);
  const [matches, setMatches] = useState<MatchInfo[]>([]);
  const [clubs, setClubs] = useState<ClubInfo[]>([]);
  const [transportations, setTransportations] = useState<TransportationInfo[]>([]);
  const [travelers, setTravelers] = useState<TravelerInfo[]>([]);
  const [staffMembers, setStaffMembers] = useState<TravelerInfo[]>([]);
  const [partners, setPartners] = useState<PartnerInfo[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [partnerDrivers, setPartnerDrivers] = useState<PartnerDriver[]>([]);

  // Navigation: based on matches sorted by date, filtered by user's preferred club
  const navInfo = useMemo(() => {
    if (!planning?.matchId || matches.length === 0) return { prevMatchId: null as number | null, nextMatchId: null as number | null };
    const clubId = clubUser?.preferredClubId;
    const clubMatches = clubId
      ? matches.filter(m => m.homeClubId === clubId || m.awayClubId === clubId)
      : matches;
    const sorted = [...clubMatches].sort((a, b) => (a.scheduledDate || a.date).localeCompare(b.scheduledDate || b.date));
    const idx = sorted.findIndex(m => m.id === planning.matchId);
    return {
      prevMatchId: idx > 0 ? sorted[idx - 1].id : null,
      nextMatchId: idx >= 0 && idx < sorted.length - 1 ? sorted[idx + 1].id : null
    };
  }, [planning, matches, clubUser?.preferredClubId]);

  const handleNavigatePrev = useCallback(() => {
    if (navInfo.prevMatchId) navigate(`/matchday-planning/new?matchId=${navInfo.prevMatchId}`);
  }, [navInfo.prevMatchId, navigate]);

  const handleNavigateNext = useCallback(() => {
    if (navInfo.nextMatchId) navigate(`/matchday-planning/new?matchId=${navInfo.nextMatchId}`);
  }, [navInfo.nextMatchId, navigate]);

  useEffect(() => {
    loadEntities();
  }, []);

  useEffect(() => {
    if (planId) {
      loadPlanningData(planId);
    } else {
      // Reset planning state when navigating to a new match – keep loading=true to avoid flicker
      setUIState(prev => ({ ...prev, isLoading: true, error: null }));
      setPlanning(null);
      setSegments([]);
      setVehicleAssignments([]);
      setStaffAssignments([]);

      const matchId = searchParams.get('matchId');
      const localMatchdayId = searchParams.get('localMatchdayId');

      if (matchId) {
        setEventType('match');
        setSelectedMatchId(parseInt(matchId));
      } else if (localMatchdayId) {
        setEventType('local');
        setSelectedLocalMatchdayId(parseInt(localMatchdayId));
      }

      // Don't set isLoading=false here – let handleCreatePlanningForEvent handle it
    }
  }, [planId, searchParams]);

  useEffect(() => {
    if (!planning && !planId && eventType) {
      if (eventType === 'match' && selectedMatchId && matches.length > 0) {
        handleCreatePlanningForEvent();
      } else if (eventType === 'local' && selectedLocalMatchdayId && localMatchdays.length > 0) {
        handleCreatePlanningForEvent();
      }
    }
  }, [eventType, selectedMatchId, selectedLocalMatchdayId, matches, localMatchdays, planning, planId]);

  const loadEntities = async () => {
    try {
      const [localMatchdaysData, matchesData, clubsData, transportationsData, travelersData, staffMembersData, partnersData, vehiclesData, partnerDriversData] = await Promise.all([
        matchdayPlanningEntitiesApi.getLocalMatchdays(),
        matchdayPlanningEntitiesApi.getMatches(),
        matchdayPlanningEntitiesApi.getClubs(),
        matchdayPlanningEntitiesApi.getTransportations(),
        matchdayPlanningEntitiesApi.getDrivers(),
        matchdayPlanningEntitiesApi.getStaffMembers(),
        matchdayPlanningEntitiesApi.getPartners(),
        matchdayPlanningEntitiesApi.getVehicles(),
        matchdayPlanningEntitiesApi.getPartnerDrivers()
      ]);

      setLocalMatchdays(localMatchdaysData);
      setMatches(matchesData);
      setClubs(clubsData);
      setTransportations(transportationsData);
      setTravelers(travelersData);
      setStaffMembers(staffMembersData);
      setPartners(partnersData);
      setVehicles(vehiclesData);
      setPartnerDrivers(partnerDriversData);
    } catch (error) {
      console.error('Error loading entities:', error);
    }
  };

  const loadPlanningData = async (id: number) => {
    try {
      setUIState(prev => ({ ...prev, isLoading: true, error: null }));

      const [planningData, segmentsData] = await Promise.all([
        matchdayPlanningApi.getPlanningById(id),
        matchdayPlanningApi.getSegments(id)
      ]);

      setPlanning(planningData);
      setSegments(segmentsData);
      setArrivalLeadTime(planningData.arrivalLeadMinutes);

      if (planningData.vehicleAssignments) setVehicleAssignments(planningData.vehicleAssignments);
      if (planningData.staffAssignments) setStaffAssignments(planningData.staffAssignments);

      setUIState(prev => ({ ...prev, isLoading: false }));
    } catch (error) {
      console.error('Error loading planning data:', error);
      setUIState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to load planning'
      }));
    }
  };

  const handleCreatePlanningForEvent = async () => {
    if (!eventType) {
      alert('Bitte wählen Sie einen Event-Typ aus.');
      return;
    }

    try {
      setUIState(prev => ({ ...prev, isLoading: true, error: null }));

      let planningName = '';
      let planningDate = '';
      let kickoffTime = '15:30';
      let localMatchdayId: number | undefined;
      let matchId: number | undefined;

      if (eventType === 'local') {
        if (!selectedLocalMatchdayId) {
          alert('Bitte wählen Sie einen lokalen Spieltag aus.');
          setUIState(prev => ({ ...prev, isLoading: false }));
          return;
        }
        const selectedEvent = localMatchdays.find(m => m.id === selectedLocalMatchdayId);
        if (!selectedEvent) {
          alert('Lokaler Spieltag nicht gefunden.');
          setUIState(prev => ({ ...prev, isLoading: false }));
          return;
        }
        planningName = `Planung: ${selectedEvent.name}`;
        planningDate = selectedEvent.date;
        localMatchdayId = selectedEvent.id;
      } else if (eventType === 'match') {
        if (!selectedMatchId) {
          alert('Bitte wählen Sie ein Spiel aus.');
          setUIState(prev => ({ ...prev, isLoading: false }));
          return;
        }
        const selectedEvent = matches.find(m => m.id === selectedMatchId);
        if (!selectedEvent) {
          alert('Spiel nicht gefunden.');
          setUIState(prev => ({ ...prev, isLoading: false }));
          return;
        }
        planningName = `Planung: ${selectedEvent.homeClub?.name || selectedEvent.homeTeamName || 'Heim'} vs ${selectedEvent.awayClub?.name || selectedEvent.awayTeamName || 'Gast'}`;
        planningDate = selectedEvent.scheduledDate;
        const matchDateTime = new Date(selectedEvent.date || selectedEvent.scheduledDate);
        kickoffTime = `${matchDateTime.getHours().toString().padStart(2, '0')}:${matchDateTime.getMinutes().toString().padStart(2, '0')}`;
        matchId = selectedEvent.id;
      }

      const existingPlanning = await matchdayPlanningApi.getPlanningByEvent(localMatchdayId, matchId);

      if (existingPlanning) {
        const segmentsData = await matchdayPlanningApi.getSegments(existingPlanning.id);
        setPlanning(existingPlanning);
        setSegments(segmentsData);
        setArrivalLeadTime(existingPlanning.arrivalLeadMinutes);
        if (existingPlanning.vehicleAssignments) setVehicleAssignments(existingPlanning.vehicleAssignments);
        if (existingPlanning.staffAssignments) setStaffAssignments(existingPlanning.staffAssignments);
        setUIState(prev => ({ ...prev, isLoading: false }));
        return;
      }

      const newPlanning = await matchdayPlanningApi.createPlanning({
        name: planningName,
        date: planningDate,
        kickoffTime,
        arrivalLeadMinutes: 90,
        localMatchdayId,
        matchId,
        notes: ''
      });

      setPlanning(newPlanning);
      setSegments(newPlanning.segments || []);
      setUIState(prev => ({ ...prev, isLoading: false }));
    } catch (error) {
      console.error('Error creating/loading planning:', error);
      setUIState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to create/load planning'
      }));
    }
  };

  const handleCreateSegment = async (formData: SegmentFormData) => {
    try {
      if (!planning) {
        alert(t('common:planning.createPlanningFirst'));
        return;
      }

      if (formData.anchorType === 'main' && segments.some(s => s.anchorType === 'main')) {
        alert('Es kann nur einen Hauptanker geben. Bitte entfernen Sie zuerst den bestehenden Hauptanker.');
        return;
      }

      const newSegment = await matchdayPlanningApi.createSegment({
        planningId: planning.id,
        location: formData.location,
        scheduledDate: formData.scheduledDate,
        scheduledTime: formData.scheduledTime,
        anchorType: formData.anchorType,
        vehicleType: formData.vehicleType,
        tripType: formData.tripType,
        stopoverDurationMinutes: formData.stopoverDurationMinutes,
        passengerCount: formData.passengerCount,
        insertPosition: formData.insertPosition,
        vehicleAssignmentId: formData.vehicleAssignmentId,
        clubId: formData.clubId,
        customLocation: formData.customLocation
      });

      setSegments(prev => [...prev, newSegment]);
      setUIState(prev => ({ ...prev, isModalOpen: false }));
      await handleRecalculate();
    } catch (error) {
      console.error('Error creating segment:', error);
      const msg = error instanceof Error ? error.message : 'Fehler beim Erstellen des Segments';
      alert(msg);
    }
  };

  const handleUpdateSegment = async (segmentId: number, updates: Partial<PlanningSegment>) => {
    try {
      const updatedSegment = await matchdayPlanningApi.updateSegment({
        id: segmentId,
        ...updates
      });

      setSegments(prev => prev.map(seg => seg.id === segmentId ? updatedSegment : seg));

      if (updates.sequenceOrder !== undefined) {
        await handleRecalculate();
      }
    } catch (error) {
      console.error('Error updating segment:', error);
      // Don't set fatal uiState.error — show a non-blocking alert instead
      const msg = error instanceof Error ? error.message : 'Fehler beim Aktualisieren des Segments';
      alert(msg);
    }
  };

  const handleDeleteSegment = async (segmentId: number) => {
    try {
      await matchdayPlanningApi.deleteSegment(segmentId);
      setSegments(prev => prev.filter(seg => seg.id !== segmentId));
    } catch (error) {
      console.error('Error deleting segment:', error);
      const msg = error instanceof Error ? error.message : 'Fehler beim Löschen des Segments';
      alert(msg);
    }
  };

  const handleSaveFavorite = async (segmentId: number, planningId: number) => {
    const name = prompt('Name für den Favoriten:');
    if (!name) return;
    try {
      await matchdayPlanningApi.saveFavorite({ name, segmentId, planningId });
      alert('Segment als Favorit gespeichert!');
    } catch (error) {
      console.error('Error saving favorite:', error);
      alert('Fehler beim Speichern des Favoriten');
    }
  };

  const handleRecalculate = async () => {
    try {
      if (!planning) return;

      await matchdayPlanningApi.updatePlanning(planning.id, {
        name: planning.name,
        date: planning.date,
        kickoffTime: planning.kickoffTime,
        arrivalLeadMinutes: arrivalLeadTime,
        localMatchdayId: planning.localMatchdayId,
        matchId: planning.matchId,
        notes: planning.notes
      });

      await matchdayPlanningApi.recalculateSegments(planning.id);

      const [freshPlanning, freshSegments] = await Promise.all([
        matchdayPlanningApi.getPlanningById(planning.id),
        matchdayPlanningApi.getSegments(planning.id)
      ]);

      setPlanning(freshPlanning);
      setSegments(freshSegments);
      setArrivalLeadTime(freshPlanning.arrivalLeadMinutes);
      if (freshPlanning.vehicleAssignments) setVehicleAssignments(freshPlanning.vehicleAssignments);
      if (freshPlanning.staffAssignments) setStaffAssignments(freshPlanning.staffAssignments);
    } catch (error) {
      console.error('Error recalculating segments:', error);
      const msg = error instanceof Error ? error.message : 'Fehler beim Neuberechnen der Segmente';
      alert(msg);
    }
  };

  const handleEditSegmentFromModal = async (formData: SegmentFormData) => {
    try {
      if (!uiState.selectedSegmentId) {
        alert('Kein Segment ausgewählt.');
        return;
      }

      const updatedSegment = await matchdayPlanningApi.updateSegment({
        id: uiState.selectedSegmentId,
        location: formData.location,
        scheduledDate: formData.scheduledDate,
        scheduledTime: formData.scheduledTime,
        anchorType: formData.anchorType,
        vehicleType: formData.vehicleType,
        tripType: formData.tripType,
        stopoverDurationMinutes: formData.stopoverDurationMinutes,
        passengerCount: formData.passengerCount,
        vehicleAssignmentId: formData.vehicleAssignmentId,
        clubId: formData.clubId,
        customLocation: formData.customLocation
      });

      setSegments(prev => prev.map(seg => seg.id === updatedSegment.id ? updatedSegment : seg));
      setUIState(prev => ({ ...prev, isModalOpen: false }));
      await handleRecalculate();
    } catch (error) {
      console.error('Error updating segment:', error);
      const msg = error instanceof Error ? error.message : 'Fehler beim Aktualisieren des Segments';
      alert(msg);
    }
  };

  const handleSavePlanning = async () => {
    try {
      if (!planning) return;

      const updatedPlanning = await matchdayPlanningApi.updatePlanning(planning.id, {
        name: planning.name,
        date: planning.date,
        kickoffTime: planning.kickoffTime,
        arrivalLeadMinutes: arrivalLeadTime,
        localMatchdayId: planning.localMatchdayId,
        matchId: planning.matchId,
        notes: planning.notes
      });

      setPlanning(updatedPlanning);
      if (updatedPlanning.vehicleAssignments) setVehicleAssignments(updatedPlanning.vehicleAssignments);
      if (updatedPlanning.staffAssignments) setStaffAssignments(updatedPlanning.staffAssignments);

      alert('Planung erfolgreich gespeichert!');
    } catch (error) {
      console.error('Error saving planning:', error);
      alert('Fehler beim Speichern: ' + (error instanceof Error ? error.message : 'Unbekannter Fehler'));
    }
  };

  const handleDeletePlanning = async () => {
    if (!planning) return;
    try {
      await matchdayPlanningApi.deletePlanning(planning.id);
      navigate('/upcoming-matches');
    } catch (error) {
      console.error('Error deleting planning:', error);
      alert('Fehler beim Löschen: ' + (error instanceof Error ? error.message : 'Unbekannter Fehler'));
    }
  };

  // ============================================
  // Vehicle Assignment Handlers
  // ============================================

  const handleAddVehicleAssignment = async (assignment: Partial<VehicleAssignment>) => {
    if (!planning) return;
    try {
      const newAssignment = await matchdayPlanningApi.createVehicleAssignment({
        planningId: planning.id,
        vehicleType: assignment.vehicleType || 'bus',
        vehicleId: assignment.vehicleId,
        transportationId: assignment.transportationId,
        partnerId: assignment.partnerId,
        travelerId: assignment.travelerId,
        partnerDriverId: assignment.partnerDriverId,
        driverType: assignment.driverType,
        displayOrder: assignment.displayOrder,
        notes: assignment.notes
      });

      const enriched: VehicleAssignment = {
        ...newAssignment,
        transportation: newAssignment.transportation ||
          (assignment.transportationId ? transportations.find(t => t.id === assignment.transportationId) : undefined),
        traveler: newAssignment.traveler ||
          (assignment.travelerId ? travelers.find(d => d.id === assignment.travelerId) : undefined),
        partner: newAssignment.partner ||
          (assignment.partnerId ? partners.find(p => p.id === assignment.partnerId) : undefined),
        partnerDriver: newAssignment.partnerDriver ||
          (assignment.partnerDriverId ? partnerDrivers.find(d => d.id === assignment.partnerDriverId) as PartnerDriverInfo | undefined : undefined)
      };

      setVehicleAssignments(prev => [...prev, enriched]);
      setBulkAssignment(enriched);
    } catch (error) {
      console.error('Error creating vehicle assignment:', error);
      alert('Fehler beim Erstellen der Fahrzeug-Zuordnung: ' + (error instanceof Error ? error.message : 'Unbekannter Fehler'));
    }
  };

  const handleUpdateVehicleAssignment = async (id: number, assignment: Partial<VehicleAssignment>) => {
    try {
      const updated = await matchdayPlanningApi.updateVehicleAssignment(id, {
        vehicleType: assignment.vehicleType,
        vehicleId: assignment.vehicleId,
        transportationId: assignment.transportationId,
        partnerId: assignment.partnerId,
        travelerId: assignment.travelerId,
        partnerDriverId: assignment.partnerDriverId,
        driverType: assignment.driverType,
        displayOrder: assignment.displayOrder,
        notes: assignment.notes
      });

      const enriched: VehicleAssignment = {
        ...updated,
        transportation: updated.transportation ||
          (assignment.transportationId ? transportations.find(t => t.id === assignment.transportationId) : undefined),
        traveler: updated.traveler ||
          (assignment.travelerId ? travelers.find(d => d.id === assignment.travelerId) : undefined),
        partner: updated.partner ||
          (assignment.partnerId ? partners.find(p => p.id === assignment.partnerId) : undefined),
        partnerDriver: updated.partnerDriver ||
          (assignment.partnerDriverId ? partnerDrivers.find(d => d.id === assignment.partnerDriverId) as PartnerDriverInfo | undefined : undefined)
      };

      setVehicleAssignments(prev => prev.map(a => a.id === id ? enriched : a));
      setBulkAssignment(enriched);
    } catch (error) {
      console.error('Error updating vehicle assignment:', error);
      alert('Fehler beim Aktualisieren der Fahrzeug-Zuordnung');
    }
  };

  const handleDeleteVehicleAssignment = async (id: number) => {
    if (!confirm('Fahrzeug-Zuordnung wirklich löschen?')) return;
    try {
      await matchdayPlanningApi.deleteVehicleAssignment(id);
      setVehicleAssignments(prev => prev.filter(a => a.id !== id));
    } catch (error) {
      console.error('Error deleting vehicle assignment:', error);
      alert('Fehler beim Löschen der Fahrzeug-Zuordnung');
    }
  };

  const handleBulkAssign = async (segmentIds: number[], assignmentId: number) => {
    // Update selected segments to have the assignment, unselected to remove it
    const eligibleSegments = segments.filter(s => !s.isMainEvent && s.anchorType !== 'main' && s.anchorType !== 'stopover');
    const updates: Promise<any>[] = [];

    for (const seg of eligibleSegments) {
      const shouldHave = segmentIds.includes(seg.id);
      const currentlyHas = seg.vehicleAssignmentId === assignmentId;

      if (shouldHave && !currentlyHas) {
        updates.push(matchdayPlanningApi.updateSegment({
          id: seg.id,
          vehicleAssignmentId: assignmentId
        }));
      } else if (!shouldHave && currentlyHas) {
        updates.push(matchdayPlanningApi.updateSegment({
          id: seg.id,
          vehicleAssignmentId: 0 // remove assignment
        }));
      }
    }

    if (updates.length > 0) {
      await Promise.all(updates);
      // Reload planning data to get fresh segments
      if (planning?.id) {
        await loadPlanningData(planning.id);
      }
    }
  };

  // ============================================
  // Staff Assignment Handlers
  // ============================================

  const handleAddStaffAssignment = async (assignment: Partial<StaffAssignment>) => {
    if (!planning) return;
    try {
      const newAssignment = await matchdayPlanningApi.createStaffAssignment({
        planningId: planning.id,
        travelerId: assignment.travelerId!,
        role: assignment.role ?? StaffRole.Physio,
        displayOrder: assignment.displayOrder,
        notes: assignment.notes
      });

      const enriched: StaffAssignment = {
        ...newAssignment,
        traveler: newAssignment.traveler ||
          (assignment.travelerId ? travelers.find(d => d.id === assignment.travelerId) : undefined)
      };

      setStaffAssignments(prev => [...prev, enriched]);
    } catch (error) {
      console.error('Error creating staff assignment:', error);
      alert('Fehler beim Erstellen der Staff-Zuordnung: ' + (error instanceof Error ? error.message : 'Unbekannter Fehler'));
    }
  };

  const handleUpdateStaffAssignment = async (id: number, assignment: Partial<StaffAssignment>) => {
    try {
      const updated = await matchdayPlanningApi.updateStaffAssignment(id, {
        travelerId: assignment.travelerId,
        role: assignment.role,
        displayOrder: assignment.displayOrder,
        notes: assignment.notes
      });

      const enriched: StaffAssignment = {
        ...updated,
        traveler: updated.traveler ||
          (assignment.travelerId ? travelers.find(d => d.id === assignment.travelerId) : undefined)
      };

      setStaffAssignments(prev => prev.map(a => a.id === id ? enriched : a));
    } catch (error) {
      console.error('Error updating staff assignment:', error);
      alert('Fehler beim Aktualisieren der Staff-Zuordnung');
    }
  };

  const handleDeleteStaffAssignment = async (id: number) => {
    if (!confirm('Staff-Zuordnung wirklich löschen?')) return;
    try {
      await matchdayPlanningApi.deleteStaffAssignment(id);
      setStaffAssignments(prev => prev.filter(a => a.id !== id));
    } catch (error) {
      console.error('Error deleting staff assignment:', error);
      alert('Fehler beim Löschen der Staff-Zuordnung');
    }
  };

  const openSegmentModal = (mode: 'create' | 'edit', segmentId?: number) => {
    setUIState(prev => ({
      ...prev,
      isModalOpen: true,
      modalMode: mode,
      selectedSegmentId: segmentId ?? null
    }));
  };

  const closeSegmentModal = () => {
    setUIState(prev => ({ ...prev, isModalOpen: false, selectedSegmentId: null }));
  };

  if (uiState.isLoading) {
    return (
      <div className="matchday-planning-loading">
        <div className="loading-spinner">{t('common:loading')}...</div>
      </div>
    );
  }

  if (uiState.error) {
    return (
      <div className="matchday-planning-error">
        <h2>{t('common:error')}</h2>
        <p>{uiState.error}</p>
        <button type="button" onClick={() => navigate('/matchday-planning')}>
          {t('common:back')}
        </button>
      </div>
    );
  }

  if (!planning) {
    if (isPartnerUser) {
      return (
        <div className="matchday-planning-page">
          <div className="event-selection-container">
            <h1><Building2 size={24} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 8 }} /> Keine Spieltagsplanung gefunden</h1>
            <p className="subtitle">Sie haben derzeit keine zugewiesenen Spieltagsplanungen.</p>
            <button type="button" onClick={() => navigate('/partner/dashboard')} className="btn btn-primary">
              Zurück zum Dashboard
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="matchday-planning-page">
        <div className="event-selection-container">
          <h1><CalendarDays size={24} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 8 }} /> {t('common:planning.createNewPlanning')}</h1>
          <p className="subtitle">{t('common:planning.selectEventSubtitle')}</p>

          <div className="event-selection-form">
            <div className="form-group">
              <label htmlFor="event-type">{t('common:planning.eventType')}</label>
              <select
                id="event-type"
                value={eventType || ''}
                onChange={(e) => setEventType(e.target.value as 'local' | 'match' | null)}
                className="form-select"
              >
                <option value="">{t('common:planning.pleaseSelect')}</option>
                <option value="local">{t('common:planning.localMatchday')}</option>
                <option value="match">{t('common:planning.officialMatch')}</option>
              </select>
            </div>

            {eventType === 'local' && (
              <div className="form-group">
                <label htmlFor="local-matchday">{t('common:planning.localMatchday')}</label>
                <select
                  id="local-matchday"
                  value={selectedLocalMatchdayId || ''}
                  onChange={(e) => setSelectedLocalMatchdayId(e.target.value ? parseInt(e.target.value) : undefined)}
                  className="form-select"
                >
                  <option value="">{t('common:planning.pleaseSelect')}</option>
                  {localMatchdays.map(matchday => (
                    <option key={matchday.id} value={matchday.id}>
                      {matchday.name} - {matchday.date}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {eventType === 'match' && (
              <div className="form-group">
                <label htmlFor="match">{t('common:planning.officialMatch')}</label>
                <select
                  id="match"
                  value={selectedMatchId || ''}
                  onChange={(e) => setSelectedMatchId(e.target.value ? parseInt(e.target.value) : undefined)}
                  className="form-select"
                >
                  <option value="">{t('common:planning.pleaseSelect')}</option>
                  {matches.map(match => (
                    <option key={match.id} value={match.id}>
                      {(match.homeClub?.name || match.homeTeamName || 'Heim')} vs {(match.awayClub?.name || match.awayTeamName || 'Gast')} - {match.scheduledDate}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="form-actions">
              <button
                type="button"
                onClick={handleCreatePlanningForEvent}
                className="btn btn-primary"
                disabled={!eventType || (eventType === 'local' && !selectedLocalMatchdayId) || (eventType === 'match' && !selectedMatchId)}
              >
                {t('common:planning.createPlanning')}
              </button>
              <button
                type="button"
                onClick={() => navigate('/matchday-planning')}
                className="btn btn-secondary"
              >
                {t('common:cancel')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="matchday-planning-page">
      <PlanningHeader
        planning={planning}
        arrivalLeadTime={arrivalLeadTime}
        onArrivalLeadTimeChange={setArrivalLeadTime}
        onSave={handleSavePlanning}
        onAddSegment={() => openSegmentModal('create')}
        onDeletePlanning={handleDeletePlanning}
        isReadOnly={isPartnerUser}
        showDrivingTimes={showDrivingTimes}
        onShowDrivingTimesChange={setShowDrivingTimes}
        onNavigatePrev={handleNavigatePrev}
        onNavigateNext={handleNavigateNext}
        hasPrev={navInfo.prevMatchId !== null}
        hasNext={navInfo.nextMatchId !== null}
      />

      <PlanningTimeline
        segments={segments}
        planningId={planning?.id || 0}
        onSegmentUpdate={handleUpdateSegment}
        onSegmentDelete={handleDeleteSegment}
        onSegmentEdit={(segmentId) => openSegmentModal('edit', segmentId)}
        isReadOnly={isPartnerUser}
        showDrivingTimes={showDrivingTimes}
        vehicleAssignments={vehicleAssignments}
        onSaveFavorite={handleSaveFavorite}
      />

      <VehicleAssignments
        assignments={vehicleAssignments}
        transportations={transportations}
        travelers={travelers}
        partnerDrivers={partnerDrivers as unknown as PartnerDriverInfo[]}
        partners={partners}
        onAdd={handleAddVehicleAssignment}
        onUpdate={handleUpdateVehicleAssignment}
        onDelete={handleDeleteVehicleAssignment}
        isReadOnly={isPartnerUser}
      />

      <StaffAssignments
        assignments={staffAssignments}
        travelers={staffMembers}
        onAdd={handleAddStaffAssignment}
        onUpdate={handleUpdateStaffAssignment}
        onDelete={handleDeleteStaffAssignment}
        isReadOnly={isPartnerUser}
      />

      {planning && segments.length >= 2 && !isPartnerUser && (
        <div className="route-map-section">
          <RouteMap
            planningId={planning.id}
            segments={segments}
            onRouteCalculated={(result) => {
              console.log('Route calculated:', result);
            }}
          />
        </div>
      )}

      {uiState.isModalOpen && (
        <SegmentModal
          mode={uiState.modalMode}
          segmentId={uiState.selectedSegmentId}
          segments={segments}
          planningId={planning?.id}
          onSubmit={uiState.modalMode === 'create' ? handleCreateSegment : handleEditSegmentFromModal}
          onClose={closeSegmentModal}
          onRefreshPlanning={() => planning?.id && loadPlanningData(planning.id)}
          clubs={clubs}
          vehicleAssignments={vehicleAssignments}
        />
      )}

      {bulkAssignment && (
        <BulkAssignmentModal
          assignment={bulkAssignment}
          segments={segments}
          onApply={handleBulkAssign}
          onClose={() => setBulkAssignment(null)}
        />
      )}
    </div>
  );
};

export default MatchdayPlanningPage;
